import { Alert } from '@billda/design-system';
import { UseFormReturn } from 'react-hook-form';
import { StepNavigation } from '../../molecules/StepNavigation';
import GangMemberSelection from '../GangMemberSelection';
import {
  GangCreationFormData,
  GangParticipant,
} from '../../../types/gangCreationTypes';
import { GangLifecycleMode } from '../../../stores/gangLifecycleStore';

export interface GangLeadersStepProps {
  form: UseFormReturn<GangCreationFormData>;
  onNext: () => void;
  onBack: () => void;
  mode: GangLifecycleMode;
  /** IDs of leaders that exist in the database (from initial gang data) */
  initialLeaderIds?: string[];
}

export const GangLeadersStep = ({
  form,
  onNext,
  onBack,
  mode,
  initialLeaderIds = [],
}: GangLeadersStepProps) => {
  const leaderIds = form.watch('leaderIds') ?? [];
  const memberIds = form.watch('memberIds') ?? [];
  const participants = form.watch('participants') ?? {};

  const hasValidSelection = leaderIds.length > 0;

  const canNavigate = hasValidSelection;

  // Detect if any persisted leaders are being removed
  const removedLeaderIds = initialLeaderIds.filter(
    id => !leaderIds.includes(id)
  );

  // Get names of all removed leaders (they remain in participants for display)
  const removedLeaderNames = removedLeaderIds
    .map(id => participants[id]?.fullName)
    .filter(Boolean);

  const hasRemovedLeaders = mode === 'edit' && removedLeaderNames.length > 0;

  const ensureParticipant = (
    userId: string,
    defaults: Pick<GangParticipant, 'fullName' | 'email' | 'canSeeSplit'>
  ) => {
    const currentParticipants = form.getValues('participants');
    const existing = currentParticipants[userId];

    const updatedParticipant: GangParticipant = {
      userId,
      fullName: defaults.fullName ?? existing?.fullName ?? '',
      email: defaults.email ?? existing?.email ?? '',
      canSeeSplit: existing?.canSeeSplit ?? defaults.canSeeSplit ?? false,
      paymentType: existing?.paymentType ?? 'percentage',
      defaultSplitPercentage: existing?.defaultSplitPercentage ?? null,
      defaultRate: existing?.defaultRate ?? null,
      defaultUnitsValue: existing?.defaultUnitsValue ?? null,
    };

    form.setValue(
      'participants',
      {
        ...currentParticipants,
        [userId]: updatedParticipant,
      },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handleSelectLeader = (user: {
    id: string;
    fullName: string;
    email: string;
  }) => {
    ensureParticipant(user.id, {
      fullName: user.fullName,
      email: user.email,
      canSeeSplit: true,
    });

    const currentLeaderIds = form.getValues('leaderIds');
    if (!currentLeaderIds.includes(user.id)) {
      form.setValue('leaderIds', [...currentLeaderIds, user.id], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    const currentMemberIds = form.getValues('memberIds');
    if (currentMemberIds.includes(user.id)) {
      form.setValue(
        'memberIds',
        currentMemberIds.filter(id => id !== user.id),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  };

  const handleRemoveLeader = (userId: string) => {
    const currentLeaderIds = form.getValues('leaderIds');
    const nextLeaderIds = currentLeaderIds.filter(id => id !== userId);
    form.setValue('leaderIds', nextLeaderIds, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const currentMemberIds = form.getValues('memberIds');

    // If user is still a member, update their canSeeSplit to false
    if (currentMemberIds.includes(userId)) {
      const nextParticipants = { ...form.getValues('participants') };
      if (nextParticipants[userId]) {
        nextParticipants[userId] = {
          ...nextParticipants[userId],
          canSeeSplit: false,
        };
        form.setValue('participants', nextParticipants, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
    // Note: Don't delete from participants - keep for warning display
    // The save logic only uses leaderIds/memberIds to build the payload
  };

  return (
    <div className="py-4">
      <p className="type-body-md mb-6 text-content-secondary">
        Choose at least one leader. Leaders gain elevated permissions and
        visibility over the gang.
      </p>

      <GangMemberSelection
        selectedIds={leaderIds}
        participants={participants}
        excludeUserIds={memberIds}
        isLeaderSelection
        onSelectUser={handleSelectLeader}
        onRemoveUser={handleRemoveLeader}
        persistedMemberIds={initialLeaderIds}
        isRemovalEnabled={true}
      />

      {leaderIds.length > 0 && (
        <Alert severity="info" className="mb-4 mt-4">
          {leaderIds.length} leader
          {leaderIds.length === 1 ? '' : 's'} selected
        </Alert>
      )}

      {hasRemovedLeaders && (
        <Alert severity="warning" className="mb-4 mt-4">
          {removedLeaderNames.length === 1
            ? `${removedLeaderNames[0]} will no longer be a leader for any pots they are involved in.`
            : `${removedLeaderNames.join(', ')} will no longer be a leader for any pots they are involved in.`}
        </Alert>
      )}

      <StepNavigation
        onPrevious={onBack}
        onNext={onNext}
        nextLabel="Next: Select Members"
        disableNext={!canNavigate}
      />
    </div>
  );
};
